import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Form,
  Button,
  Input,
  Spin,
  Table,
  Modal,
  message,
} from "antd";

import AdminHeader from "../../components/AdminHeader";
import { showErrorModal } from "../../components/Modal";
import { EditOutlined } from "@ant-design/icons";
import { useStoreProfile } from "../../api/useStores";
import { StoreProfile } from "../../models";
import { useAuth } from "../../api/Auth";

const StoreManager: React.FC = () => {
  const [editingStore, setEditingStore] = useState<Partial<StoreProfile>>({});
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm();

  const { currentUser } = useAuth()

  const {
    storeProfiles,
    loading,
    getStoreProfiles,
    getStoreProfile,
    addStoreProfile,
    updateStoreProfile,
  } = useStoreProfile();

  const userId = currentUser?.id;

  useEffect(() => {
    getStoreProfiles();
  }, [getStoreProfiles]);

  const handleSubmit = () => {
    const storeProfile = { ...editingStore, owner_id: userId };

    if (isAdd) {
      addStoreProfile(storeProfile as StoreProfile)
        .then(() => {
          getStoreProfiles();
          setEditModalVisible(false);
          message.success("Store added successfully");
        })
        .catch(() => showErrorModal("Failed to add store"));
    } else {
      updateStoreProfile(storeProfile as StoreProfile)
        .then(() => {
          getStoreProfiles();
          setEditModalVisible(false);
          message.success("Store updated successfully");
        })
        .catch(() => showErrorModal("Failed to update store"));
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: StoreProfile) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              getStoreProfile(record.id.toString())
                .then((store: StoreProfile) => {
                  setEditingStore(store);
                  form.setFieldsValue(store);
                })
                .catch(() => {
                  setEditingStore(record);
                  form.setFieldsValue(record);
                })
                .finally(() => {
                  setIsAdd(false);
                  setEditModalVisible(true);
                });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <AdminHeader>
      <Container>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Manage Stores</h1>
            </div>
            <div className="mb-4 flex justify-end gap-2">
              <Button
                type="primary"
                onClick={() => {
                  setEditingStore({});
                  form.resetFields();
                  setIsAdd(true);
                  setEditModalVisible(true);
                }}
              >
                Add Store
              </Button>
            </div>
            <Table
              loading={loading}
              rowKey="id"
              columns={columns}
              dataSource={storeProfiles}
              style={{ maxWidth: "100%" }}
            />
          </div>
        </div>

        {/* store edit modal */}
        <Modal
          title={(isAdd ? "Add" : "Edit") + " Store"}
          open={isEditModalVisible}
          onOk={handleSubmit}
          onCancel={() => setEditModalVisible(false)}
          width={600}
          okButtonProps={{ disabled: !isFormValid }}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={editingStore}
            onValuesChange={(_, allValues) => setEditingStore({ ...editingStore, ...allValues })}
            onFieldsChange={(_, allFields) => {
              const hasError = form.getFieldsError().some(({ errors }) => errors.length);
              setIsFormValid(!hasError);
            }}
          >
            <Form.Item
              label="Store Name"
              name="name"
              rules={[{ required: true, message: "Please input the store name!" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Page Title(optional)"
              name="title"
              rules={[{ required: false}]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Container>
    </AdminHeader>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 80vh;
`;

export default StoreManager;
